import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, Loader2, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { CALENDLY_URL, OFFICES } from "@/content";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Market Opportunity Intelligence",
  "Target Account Research",
  "Decision-Maker Research",
  "Business Development Advisory",
  "Market Entry",
  "Other",
];

const EMPTY = { name: "", company: "", email: "", phone: "", topic: "", message: "" };

const inputCls =
  "w-full border border-white/15 bg-navy-light/60 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 transition-colors duration-300 focus:border-gold/60 focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone || null,
        topic: form.topic,
        message: form.message,
      });
      setSent(true);
      setForm(EMPTY);
      toast.success("Thank you. Your enquiry has been received. We will respond directly.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact | Let's Talk About Your Market | Thrive Business Development"
        description="Contact Thrive Business Development to discuss market mapping, target account research, decision-maker research or business development advisory for energy and technical markets."
      />

      <section className="relative overflow-hidden pt-40 pb-16 sm:pt-48 sm:pb-20" data-testid="contact-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Contact</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Let's Talk About Your Market
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              Whether you want to enter a new market, understand your target clients, strengthen
              your pipeline or identify the right decision-makers, we would be happy to discuss what
              you are trying to achieve.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-call-link"
              className="group mt-8 inline-flex items-center gap-2 border border-gold/50 px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy"
            >
              Or book a call directly
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-testid="contact-offices-section">
        <Reveal>
          <p className="eyebrow mb-8">Our Offices</p>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {OFFICES.map((o, i) => (
            <Reveal key={o.id} delay={i * 0.08}>
              <address className="group h-full border border-white/10 bg-navy-light/40 p-8 not-italic transition-colors duration-300 hover:border-gold/40" data-testid={`contact-office-${o.id}`}>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border border-gold/40 text-gold" aria-hidden="true">
                    <MapPin size={16} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-display text-base md:text-lg font-bold tracking-[-0.02em] text-white" data-testid={`contact-office-${o.id}-city`}>{o.city}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid={`contact-office-${o.id}-address`}>
                      {o.lines.map((l, j) => (
                        <span key={l} className="block">{l}</span>
                      ))}
                    </p>
                    <a
                      href={o.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-office-${o.id}-maps-link`}
                      className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors duration-300 hover:text-gold-glow"
                    >
                      View on Google Maps
                      <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>
                </div>
              </address>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="contact-form-section">
        <div className="grid gap-14 lg:grid-cols-[1fr_360px] lg:gap-20">
          <Reveal>
            {sent ? (
              <div className="border border-gold/40 bg-gold/5 p-10" data-testid="contact-success-message">
                <p className="eyebrow mb-4">Enquiry Received</p>
                <h2 className="font-display text-2xl font-semibold text-white">Thank you.</h2>
                <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
                  Your enquiry has been received and we will respond to you directly. If you have
                  another question in the meantime, you are welcome to send a further message.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => setSent(false)}
                  className="mt-8 border border-white/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-200 transition-colors hover:border-gold/60 hover:text-gold"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      required
                      value={form.name}
                      onChange={set("name")}
                      className={inputCls}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                      Company
                    </label>
                    <input
                      id="contact-company"
                      data-testid="contact-company-input"
                      required
                      value={form.company}
                      onChange={set("company")}
                      className={inputCls}
                      placeholder="Your company"
                      autoComplete="organization"
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      className={inputCls}
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                      Phone <span className="text-slate-600 normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls}
                      placeholder="Optional"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-topic" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    What can we help with?
                  </label>
                  <select
                    id="contact-topic"
                    data-testid="contact-topic-select"
                    required
                    value={form.topic}
                    onChange={set("topic")}
                    className={`${inputCls} appearance-none ${form.topic ? "" : "text-slate-600"}`}
                  >
                    <option value="" disabled>
                      Select a topic
                    </option>
                    {TOPICS.map((t) => (
                      <option key={t} value={t} className="bg-navy-light text-slate-100">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-textarea"
                    required
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputCls} resize-y`}
                    placeholder="Tell us what you are trying to achieve"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    className="group inline-flex items-center gap-2 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        Sending <Loader2 size={15} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        Start a Conversation
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="mt-5 text-xs text-slate-500" data-testid="contact-privacy-note">
                    Your details will only be used to respond to your enquiry.
                  </p>
                </div>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12}>
            <aside className="border border-white/10 bg-navy-light/40 p-8">
              <p className="eyebrow-blue mb-8">What Happens Next</p>
              <ol className="space-y-7">
                {[
                  ["01", "We review your enquiry and the market context around it."],
                  ["02", "We respond directly to arrange an initial conversation."],
                  ["03", "Together we define where better intelligence would help most."],
                ].map(([n, text]) => (
                  <li key={n} className="flex gap-4">
                    <span className="font-mono text-xs text-gold/70">{n}</span>
                    <span className="text-sm text-slate-400 leading-relaxed">{text}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-10 border-t border-white/10 pt-6">
                <p className="text-xs leading-relaxed text-slate-500">
                  Thrive works with businesses across energy, engineering and technical B2B markets,
                  from early market entry questions to focused account targeting.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
